import { useEffect, useRef, useState } from 'react'
import { X, Film, Image as ImageIcon } from 'lucide-react'

export interface MediaPreviewData {
  type: 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'file'
  src: string
  title?: string
  subtitle?: string
  contentType?: string | null
  textContent?: string | null
  /** Start/end seconds for clip-bounded video playback. When set, the
   *  video seeks to startTime on load and pauses at endTime. */
  startTime?: number
  endTime?: number
}

interface MediaPreviewModalProps {
  preview: MediaPreviewData | null
  onClose: () => void
}

export default function MediaPreviewModal({ preview, onClose }: MediaPreviewModalProps) {
  const [videoFailed, setVideoFailed] = useState(false)
  const [embedFailed, setEmbedFailed] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (!preview) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [preview, onClose])

  useEffect(() => {
    setVideoFailed(false)
    setEmbedFailed(false)
  }, [preview?.src, preview?.type])

  // Clip-bounded playback: seek to startTime on load, pause at endTime.
  const startTime = preview?.startTime
  const endTime = preview?.endTime
  const handleVideoLoadedMetadata = () => {
    const video = videoRef.current
    if (!video) return
    if (typeof startTime === 'number' && startTime > 0) {
      try { video.currentTime = startTime } catch { /* seek failures are non-fatal */ }
    }
  }
  const handleVideoTimeUpdate = () => {
    const video = videoRef.current
    if (!video) return
    if (typeof endTime === 'number' && video.currentTime >= endTime - 0.05) {
      video.pause()
    }
  }

  if (!preview) return null

  return (
    <div className="rq-modal-overlay" onClick={onClose}>
      <div
        className="bg-[var(--rq-surface)] rounded-xl border border-white/10 shadow-2xl flex flex-col"
        style={{ width: '90vw', maxWidth: '1200px', height: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {preview.type === 'video' ? (
              <Film className="w-4 h-4 text-[var(--rq-text-soft)]" />
            ) : (
              <ImageIcon className="w-4 h-4 text-[var(--rq-text-soft)]" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[var(--rq-text)] truncate">{preview.title || 'Preview'}</p>
              {preview.subtitle && <p className="text-xs text-[var(--rq-text-soft)] truncate">{preview.subtitle}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 p-4 flex items-center justify-center bg-black/25">
          {preview.type === 'video' && !videoFailed ? (
            <video
              ref={videoRef}
              src={preview.src}
              controls
              autoPlay
              className="max-w-full max-h-full rounded-lg bg-black"
              onError={() => setVideoFailed(true)}
              onLoadedMetadata={handleVideoLoadedMetadata}
              onTimeUpdate={handleVideoTimeUpdate}
            />
          ) : preview.type === 'video' ? (
            <div className="text-center px-6">
              <p className="text-sm text-[var(--rq-text)] mb-2">Video preview unavailable in browser</p>
              <p className="text-xs text-[var(--rq-text-soft)]">
                This video codec is not supported for direct playback. Try opening a transcoded proxy or download the source.
              </p>
            </div>
          ) : preview.type === 'audio' ? (
            <audio
              src={preview.src}
              controls
              autoPlay
              className="w-full max-w-2xl"
            />
          ) : preview.type === 'pdf' && !embedFailed ? (
            <iframe
              src={preview.src}
              title={preview.title || 'PDF preview'}
              className="w-full h-full rounded-lg bg-white"
              onError={() => setEmbedFailed(true)}
            />
          ) : preview.type === 'text' ? (
            <div className="w-full h-full overflow-auto rounded-lg border border-white/10 bg-black/20 p-4">
              <pre className="text-xs text-[var(--rq-text)] whitespace-pre-wrap break-words font-mono leading-relaxed">
                {preview.textContent || 'No text preview available.'}
              </pre>
            </div>
          ) : preview.type === 'file' || embedFailed ? (
            <div className="text-center px-6">
              <p className="text-sm text-[var(--rq-text)] mb-2">Preview unavailable in browser</p>
              <a
                href={preview.src}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[var(--rq-blue)] hover:underline"
              >
                Open file in a new tab
              </a>
            </div>
          ) : (
            <img
              src={preview.src}
              alt={preview.title || 'Preview'}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          )}
        </div>
      </div>
    </div>
  )
}
